import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ApiError } from "../../lib/api";
import { registerAccount } from "../../lib/auth";

interface FormState {
  email: string;
  password: string;
  name: string;
  tenant_name: string;
  accept_terms: boolean;
}

const INITIAL: FormState = {
  email: "",
  password: "",
  name: "",
  tenant_name: "",
  accept_terms: false,
};

export function SignupPage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function update<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await registerAccount(form);
      navigate("/verify-email", {
        replace: true,
        state: {
          email: result.verification_email_sent_to,
          devCode: result.verification_code ?? null,
        },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.detail || err.problem.title);
      } else {
        setError("Sign-up failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-6 px-6 py-12">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-neutral-900">Create your account</h1>
        <p className="text-sm text-neutral-600">
          Free tier — 50 AI generations / month, no credit card.
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
        />
        <Input
          label="Your name"
          autoComplete="name"
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
        />
        <Input
          label="Workspace name"
          required
          hint="Use your company or team name. You can change it later."
          value={form.tenant_name}
          onChange={(e) => update("tenant_name", e.target.value)}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          minLength={12}
          hint="Min 12 characters."
          value={form.password}
          onChange={(e) => update("password", e.target.value)}
        />
        <label className="flex items-start gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-neutral-300"
            checked={form.accept_terms}
            onChange={(e) => update("accept_terms", e.target.checked)}
            required
          />
          <span>
            I agree to the{" "}
            <a
              href="https://email-builder.dev/terms"
              className="text-primary-600 hover:underline"
              rel="noreferrer"
              target="_blank"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="https://email-builder.dev/privacy"
              className="text-primary-600 hover:underline"
              rel="noreferrer"
              target="_blank"
            >
              Privacy Policy
            </a>
            .
          </span>
        </label>

        {error !== null && (
          <p
            role="alert"
            className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
          >
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" fullWidth loading={submitting}>
          {submitting ? "Creating account…" : "Create account"}
        </Button>
      </form>

      <p className="text-center text-sm text-neutral-600">
        Already have an account?{" "}
        <Link to="/login" className="text-primary-600 hover:underline">
          Sign in
        </Link>
      </p>
    </main>
  );
}
